import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BorderColorProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
} from 'styled-system';
import {
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  compose,
  display,
  size,
  space,
  themeGet,
} from 'styled-system';

import { Button } from 'common/components/Button';
import Icon from 'common/components/Icon';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { SpaceProps } from 'common/theme/system-types';
import { fill } from 'common/theme/system-utilities';
import { trackHamburgerClick } from '../tracking';

const ICON_SIZE = 48;

const MenuButtonContainer = styled(Button)<
  BorderColorProps &
    BorderRadiusProps &
    BorderStyleProps &
    BorderWidthProps &
    DisplayProps &
    SpaceProps
>`
  ${compose(borderColor, borderRadius, borderStyle, borderWidth, display, space)}

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
  }

  &:active {
    outline: ${themeGet('borderWidths.medium')} solid
      ${themeGet('colors.sys.primary.border.default')};
    outline-offset: -${themeGet('borderWidths.medium')};
  }

  &:focus-visible {
    outline-offset: -${themeGet('borderWidths.medium')};
  }
`;

const menuButtonContainerStyles: ComponentProps<typeof MenuButtonContainer> = {
  kind: 'bare',
  borderRadius: 'small',
  display: 'inline-flex',
  mr: ['sp_12', null, 'sp_16'],
};

const BurgerIcon = styled(Icon)`
  ${fill};
  ${size};
`;

const burgerIconStyles: Partial<ComponentProps<typeof BurgerIcon>> = {
  fill: 'sys.neutral.icon.active',
  size: ICON_SIZE,
};

interface MenuButtonProps {
  onClick: () => void;
  withBorder?: boolean;
}

const MenuButton = ({ onClick, withBorder = false }: MenuButtonProps) => {
  const { t } = useLocalization();

  return (
    <MenuButtonContainer
      aria-label={t('h24_open_menu')}
      aria-haspopup="dialog"
      aria-controls="side-navigation"
      onClick={() => {
        trackHamburgerClick();
        onClick();
      }}
      {...menuButtonContainerStyles}
      {...(withBorder && {
        borderWidth: 'small',
        borderStyle: 'solid',
        borderColor: 'sys.neutral.border.inactive',
      })}
    >
      <BurgerIcon name="burger" {...burgerIconStyles} />
    </MenuButtonContainer>
  );
};

export default MenuButton;
